import { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer,
} from 'recharts';
import AdminLayout from '../../components/AdminLayout/AdminLayout';
import StatCard from '../../components/StatCard/StatCard';
import styles from './Dashboard.module.css';
import UsersIcon from '../../icons/UsersIcon';
import PackageIcon from '../../icons/PackageIcon';
import HexagonExclamationIcon from '../../icons/HexagonExclamationIcon';
import DollarSackIcon from '../../icons/DollarSackIcon';
import GavelIcon from '../../icons/GavelIcon';
import {
  fetchOrders, fetchPartners, fetchBatches, fetchLoads, fetchPayments, fetchDisputes,
} from '../../lib/directus';

/* ─── helpers ─── */
function formatMoney(n) {
  if (n >= 1_000_000) return `R${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `R${(n / 1_000).toFixed(1)}K`;
  return `R${n.toLocaleString()}`;
}

function formatRelativeTime(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} minute${mins !== 1 ? 's' : ''} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs !== 1 ? 's' : ''} ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function getWeekBounds() {
  const now = new Date();
  const dow = now.getDay();
  const monday = new Date(now);
  monday.setDate(now.getDate() - (dow === 0 ? 6 : dow - 1));
  monday.setHours(0, 0, 0, 0);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { monday, sunday };
}

function toNiceCap(n) {
  if (n <= 5)  return 5;
  if (n <= 10) return 10;
  return Math.ceil(n / 5) * 5;
}

function buildChartData(orders, period) {
  const now = new Date();

  if (period === 'Daily') {
    const labels = ['12am', '3am', '6am', '9am', '12pm', '3pm', '6pm', '9pm'];
    const counts = new Array(8).fill(0);
    const dayStart = new Date(now);
    dayStart.setHours(0, 0, 0, 0);
    orders.forEach(o => {
      if (!o.date_created) return;
      const d = new Date(o.date_created);
      if (d >= dayStart) counts[Math.min(Math.floor(d.getHours() / 3), 7)]++;
    });
    const cap = toNiceCap(Math.max(...counts, 1));
    return labels.map((day, i) => ({ day, value: counts[i], remainder: Math.max(0, cap - counts[i]) }));
  }

  if (period === 'Weekly') {
    const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const { monday } = getWeekBounds();
    const counts = new Array(7).fill(0);
    orders.forEach(o => {
      if (!o.date_created) return;
      const diff = Math.floor((new Date(o.date_created) - monday) / 86_400_000);
      if (diff >= 0 && diff < 7) counts[diff]++;
    });
    const cap = toNiceCap(Math.max(...counts, 1));
    return DAYS.map((day, i) => ({ day, value: counts[i], remainder: Math.max(0, cap - counts[i]) }));
  }

  if (period === 'Monthly') {
    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const numWeeks = daysInMonth <= 28 ? 4 : 5;
    const labels = Array.from({ length: numWeeks }, (_, i) => `Wk ${i + 1}`);
    const counts = new Array(numWeeks).fill(0);
    orders.forEach(o => {
      if (!o.date_created) return;
      const d = new Date(o.date_created);
      if (d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()) {
        counts[Math.min(Math.floor((d.getDate() - 1) / 7), numWeeks - 1)]++;
      }
    });
    const cap = toNiceCap(Math.max(...counts, 1));
    return labels.map((day, i) => ({ day, value: counts[i], remainder: Math.max(0, cap - counts[i]) }));
  }

  // Yearly
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const counts = new Array(12).fill(0);
  orders.forEach(o => {
    if (!o.date_created) return;
    const d = new Date(o.date_created);
    if (d.getFullYear() === now.getFullYear()) counts[d.getMonth()]++;
  });
  const cap = toNiceCap(Math.max(...counts, 1));
  return MONTHS.map((day, i) => ({ day, value: counts[i], remainder: Math.max(0, cap - counts[i]) }));
}

function getDateRangeLabel(period) {
  const now = new Date();
  const fmt = d => d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  if (period === 'Daily') {
    return now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  }
  if (period === 'Weekly') {
    const { monday, sunday } = getWeekBounds();
    return `${fmt(monday)} – ${fmt(sunday)}`;
  }
  if (period === 'Monthly') {
    return now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }
  return String(now.getFullYear());
}

function buildActivity(orders, batches) {
  const items = [];

  orders.slice(0, 8).forEach(o => {
    const batch = o.batch || {};
    const buyer = o.buyer?.company_name || 'A buyer';
    const commodity = batch.commodity || 'commodity';
    items.push({ dot: '#9DC0DC', bold: buyer, rest: ` placed an order for ${commodity}`, time: o.date_created });
  });

  batches.slice(0, 8).forEach(b => {
    const seller = b.seller?.company_name || 'A seller';
    const commodity = b.commodity || 'commodity';
    const qty = b.quantity ? ` (${b.quantity} MT)` : '';
    items.push({ dot: '#A1A920', bold: seller, rest: ` listed a new batch of ${commodity}${qty}`, time: b.date_created });
  });

  items.sort((a, b) => new Date(b.time) - new Date(a.time));
  return items.slice(0, 5);
}

function badgeText(label, count) {
  if (count === undefined) return '...';
  if (count === 0) return 'None pending';
  switch (label) {
    case 'Partner applications': return `${count} pending review`;
    case 'Batch approvals':      return `${count} pending review`;
    case 'Bid selection':        return `${count} awaiting winner`;
    case 'Open disputes':        return count === 1 ? '1 needs resolution' : `${count} need resolution`;
    case 'Payments':             return `${count} ready for release`;
    default:                     return `${count} pending`;
  }
}

const PERIODS = ['Daily', 'Weekly', 'Monthly', 'Yearly'];

/* ─── Sub-components ─── */
function OrderVolumeChart({ orders }) {
  const [period, setPeriod] = useState('Weekly');

  const chartData = buildChartData(orders, period);
  const cap = Math.max(...chartData.map(d => d.value + d.remainder), 1);
  const step = Math.max(1, Math.round(cap / 4));
  const ticks = [0, step, step * 2, step * 3, cap];

  const barSize = period === 'Yearly' ? 28 : period === 'Daily' ? 42 : 60;

  return (
    <div className={`${styles.panel} h-100`}>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <p className={styles.panelTitle}>Order volume</p>
        <div className={styles.chartControls}>
          <span className={styles.dateRange}>{getDateRangeLabel(period)}</span>
          <select
            className={styles.periodSelect}
            value={period}
            onChange={e => setPeriod(e.target.value)}
          >
            {PERIODS.map(p => <option key={p}>{p}</option>)}
          </select>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={chartData} barSize={barSize} stackOffset="none" margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
          <CartesianGrid horizontal={false} vertical={false} />
          <XAxis dataKey="day" axisLine={{ stroke: '#e8e8e8' }} tickLine={false} tick={{ fontSize: 13, fill: '#969696', fontFamily: 'General Sans, sans-serif', fontWeight: 400, textAnchor: 'middle' }} />
          <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 13, fill: '#969696', fontFamily: 'General Sans, sans-serif', fontWeight: 400 }} ticks={ticks} domain={[0, cap]} allowDecimals={false} />
          <Bar dataKey="value" stackId="a" fill="#A1A920" radius={0} isAnimationActive={false} />
          <Bar dataKey="remainder" stackId="a" fill="#B4B4B433" radius={0} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function ActionRequired({ counts }) {
  const ITEMS = [
    { label: 'Partner applications', icon: <UsersIcon /> },
    { label: 'Batch approvals',      icon: <PackageIcon /> },
    { label: 'Bid selection',        icon: <GavelIcon /> },
    { label: 'Open disputes',        icon: <HexagonExclamationIcon /> },
    { label: 'Payments',              icon: <DollarSackIcon /> },
  ];
  const COUNT_KEYS = ['pendingPartners', 'pendingBatches', 'awaitingBids', 'openDisputes', 'pendingPayments'];

  return (
    <div className={`${styles.actionPanel} h-100`}>
      <p className={styles.actionPanelTitle}>Action required</p>
      {ITEMS.map((item, i) => (
        <div key={i} className={styles.actionRow}>
          <div className={styles.actionLeft}>
            <div className={styles.actionIconBox}>{item.icon}</div>
            {item.label}
          </div>
          <span className={styles.badge}>{badgeText(item.label, counts[COUNT_KEYS[i]])}</span>
        </div>
      ))}
    </div>
  );
}

function RecentActivity({ activity }) {
  return (
    <div className={styles.activityCard}>
      <p className={styles.activityTitle}>Recent activity</p>
      {activity.length === 0 ? (
        <div className={styles.activityItem}>
          <span className={styles.activityText} style={{ color: '#9ca3af' }}>No recent activity</span>
        </div>
      ) : (
        activity.map((item, i) => (
          <div key={i} className={styles.activityItem}>
            <div className={styles.activityRow}>
              <span className={styles.dot} style={{ backgroundColor: item.dot }} />
              <span className={styles.activityText}>
                <span className={styles.activityBold}>{item.bold}</span>{item.rest}
              </span>
            </div>
            <span className={styles.activityTime}>{formatRelativeTime(item.time)}</span>
          </div>
        ))
      )}
    </div>
  );
}

/* ─── Page ─── */
export default function Dashboard() {
  const [orders, setOrders]     = useState([]);
  const [partners, setPartners] = useState([]);
  const [batches, setBatches]   = useState([]);
  const [loads, setLoads]       = useState([]);
  const [payments, setPayments] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    Promise.allSettled([
      fetchOrders(),
      fetchPartners(),
      fetchBatches(),
      fetchLoads(),
      fetchPayments(),
      fetchDisputes(),
    ]).then(([ordersRes, partnersRes, batchesRes, loadsRes, paymentsRes, disputesRes]) => {
      if (ordersRes.status   === 'fulfilled') setOrders(ordersRes.value);
      if (partnersRes.status === 'fulfilled') setPartners(partnersRes.value);
      if (batchesRes.status  === 'fulfilled') setBatches(batchesRes.value);
      if (loadsRes.status    === 'fulfilled') setLoads(loadsRes.value);
      if (paymentsRes.status === 'fulfilled') setPayments(paymentsRes.value);
      if (disputesRes.status === 'fulfilled') setDisputes(disputesRes.value);
      setLoading(false);
    });
  }, []);

  const totalOrders    = orders.length;
  const gmv            = orders.reduce((sum, o) => {
    const b = o.batch || {};
    return sum + (parseFloat(b.quantity) || 0) * (parseFloat(b.unit_price) || 0);
  }, 0);
  const activePartners = partners.filter(p => p.status === 'approved').length;
  const revenue        = payments
    .filter(p => p.status === 'cleared')
    .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);

  const counts = loading ? {} : {
    pendingPartners: partners.filter(p => p.status === 'pending').length,
    pendingBatches:  batches.filter(b => ['Pending', 'pending'].includes(b.status)).length,
    awaitingBids:    loads.filter(l => !l.awarded_transporter && (l.bids || []).length > 0).length,
    openDisputes:    disputes.filter(d => ['Open', 'open'].includes(d.status)).length,
    pendingPayments: payments.filter(p => p.status === 'approved').length,
  };

  const activity = buildActivity(orders, batches);

  return (
    <AdminLayout title="Dashboard">
      <div className="row g-3 mb-3">
        <div className="col-6 col-lg-3">
          <StatCard label="Total orders" value={loading ? '—' : String(totalOrders)} />
        </div>
        <div className="col-6 col-lg-3">
          <StatCard label="GMV" value={loading ? '—' : formatMoney(gmv)} />
        </div>
        <div className="col-6 col-lg-3">
          <StatCard label="Active partners" value={loading ? '—' : String(activePartners)} />
        </div>
        <div className="col-6 col-lg-3">
          <StatCard label="Revenue" value={loading ? '—' : formatMoney(revenue)} />
        </div>
      </div>

      <div className="row g-3 mb-3">
        <div className="col-12 col-lg-7">
          <OrderVolumeChart orders={orders} />
        </div>
        <div className="col-12 col-lg-5">
          <ActionRequired counts={counts} />
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <RecentActivity activity={activity} />
        </div>
      </div>
    </AdminLayout>
  );
}
